/**
 * Rate limiter for API requests
 */

import type { RateLimitConfig } from '../types'

/**
 * Token bucket rate limiter
 */
export class RateLimiter {
  private tokens: number
  private lastRefill: number
  private config: Required<RateLimitConfig>

  constructor(config: RateLimitConfig) {
    this.config = {
      maxRequests: config.maxRequests,
      perMilliseconds: config.perMilliseconds,
      maxRPS: config.maxRPS || Math.ceil(config.maxRequests / (config.perMilliseconds / 1000)),
    }

    this.tokens = this.config.maxRequests
    this.lastRefill = Date.now()
  }

  /**
   * Acquires a token (waits if necessary)
   */
  async acquire(): Promise<void> {
    this.refillTokens()

    if (this.tokens >= 1) {
      this.tokens--
      return
    }

    // Wait for next token
    const waitTime = this.calculateWaitTime()
    if (waitTime > 0) {
      await this.delay(waitTime)
      return this.acquire()
    }
  }

  /**
   * Tries to acquire a token without waiting
   */
  tryAcquire(): boolean {
    this.refillTokens()

    if (this.tokens >= 1) {
      this.tokens--
      return true
    }

    return false
  }

  /**
   * Gets the current number of available tokens
   */
  getAvailableTokens(): number {
    this.refillTokens()
    return Math.floor(this.tokens)
  }

  /**
   * Gets the time until next token is available
   */
  getTimeUntilNextToken(): number {
    this.refillTokens()

    if (this.tokens >= 1) {
      return 0
    }

    return this.calculateWaitTime()
  }

  /**
   * Updates rate limit configuration
   */
  updateConfig(config: Partial<RateLimitConfig>): void {
    this.config = {
      ...this.config,
      ...config,
      maxRPS:
        config.maxRPS ||
        Math.ceil(
          (config.maxRequests || this.config.maxRequests) /
            ((config.perMilliseconds || this.config.perMilliseconds) / 1000)
        ),
    }

    // Reset tokens to new maximum
    this.tokens = Math.min(this.tokens, this.config.maxRequests)
  }

  /**
   * Gets current configuration
   */
  getConfig(): RateLimitConfig {
    return { ...this.config }
  }

  /**
   * Resets the rate limiter
   */
  reset(): void {
    this.tokens = this.config.maxRequests
    this.lastRefill = Date.now()
  }

  /**
   * Gets rate limiter statistics
   */
  getStats(): {
    availableTokens: number
    maxTokens: number
    refillRate: number
    timeUntilNextToken: number
  } {
    return {
      availableTokens: this.getAvailableTokens(),
      maxTokens: this.config.maxRequests,
      refillRate: this.config.maxRequests / this.config.perMilliseconds,
      timeUntilNextToken: this.getTimeUntilNextToken(),
    }
  }

  /**
   * Refills tokens based on elapsed time
   */
  private refillTokens(): void {
    const now = Date.now()
    const elapsed = now - this.lastRefill

    if (elapsed > 0) {
      const tokensToAdd = (elapsed / this.config.perMilliseconds) * this.config.maxRequests
      this.tokens = Math.min(this.config.maxRequests, this.tokens + tokensToAdd)
      this.lastRefill = now
    }
  }

  /**
   * Calculates wait time for next token
   */
  private calculateWaitTime(): number {
    const tokensNeeded = 1 - this.tokens
    const timePerToken = this.config.perMilliseconds / this.config.maxRequests
    return Math.ceil(tokensNeeded * timePerToken)
  }

  /**
   * Creates a delay promise
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Creates a rate limiter for requests per second
   */
  static perSecond(requestsPerSecond: number): RateLimiter {
    return new RateLimiter({
      maxRequests: requestsPerSecond,
      perMilliseconds: 1000,
    })
  }

  /**
   * Creates a rate limiter for requests per minute
   */
  static perMinute(requestsPerMinute: number): RateLimiter {
    return new RateLimiter({
      maxRequests: requestsPerMinute,
      perMilliseconds: 60000,
    })
  }

  /**
   * Creates a rate limiter for requests per hour
   */
  static perHour(requestsPerHour: number): RateLimiter {
    return new RateLimiter({
      maxRequests: requestsPerHour,
      perMilliseconds: 3600000,
    })
  }

  /**
   * Creates a burst rate limiter (allows bursts up to maxBurst, then limits to sustainedRate)
   */
  static burst(
    maxBurst: number,
    sustainedRate: number,
    perMilliseconds = 1000
  ): RateLimiter {
    return new RateLimiter({
      maxRequests: maxBurst,
      perMilliseconds,
      maxRPS: sustainedRate,
    })
  }
}
